import { useAppDispatch, useAppSelector } from 'appState/store'
import VaCustomAvatar from 'assets/images/icons/VaCustomAvatar.svg?react'
import { apiPaths, RefreshIntervalsEnum } from 'configs/apiConfigs'
import { startCall } from 'features/calls/callsApi'
import { useCall } from 'features/calls/callsHooks'
import { updateHumanAgentCallInProgress, updateLiveCall } from 'features/calls/callsSlice'
import {
  AutomationTypeEnum,
  CallStatusEnum,
  CallTypeEnum,
  ICall,
  UseCaseEnum,
  UseCaseEnumToUseCaseIdEnum,
} from 'features/calls/types'
import { getPayerAvailability } from 'features/claims/claimsApi'
import { callSupportedOperations, ClaimStatusTypeEnum, IClaim, ILevelOfSupport } from 'features/claims/types'
import { getClaimCallPath } from 'features/claims/utils/claimsPathUtils'
import { useRevalidateAllSwrCache } from 'hooks/useRevalidateAllSwrCache'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getTntIdForNavigation } from 'utils/tenantUrlUtils'
import { Button, OBDropdown } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { ErrorMessagesEnum } from 'types/errorTypes'
import { sendLogs } from 'utils'
import { formatCallTypeLabel } from 'utils/cellFormat'
import { errorHandler } from 'utils/errors/errors'
import { notificator } from 'utils/notificator/notificator'

import { getUnblockMicHelperUrl } from 'features/claims/utils/misc'
import { useUserPreference, useUserProfile } from 'features/settings/settingsHooks'
import { DEFAULT_PAYER_VA_SOFTPHONE_KEY, UserPreferencesKeysEnum } from 'features/settings/settingsTypes'
import { useMedia } from 'hooks/useMedia'
import { getBrowser } from 'utils/device'
import { EnvironmentFeaturesEnabled } from '../../../../configs/environmentFeatures'
import { ClaimCallJobStandardModal } from './ClaimCallJobStandardModal'
import { ClaimCallJobStartedModal } from './ClaimCallJobStartedModal'
import { PayerCallRequestConfirmationModal } from './PayerCallRequestConfirmationModal'

interface IProps {
  claim: IClaim
  activeCallJobId: string | null
  disabled?: boolean
  phoneNumber: string
}

// see OM-6302
export const ClaimCallMenu = ({ claim, activeCallJobId, phoneNumber }: IProps) => {
  const revalidateAllSwrCache = useRevalidateAllSwrCache()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { microphonePermissionStatus } = useMedia()
  const redirectToLiveCalls = (activeCallJobId: string) => {
    const tenantId = getTntIdForNavigation()
    navigate(getClaimCallPath(tenantId, claim.oaiClaimId, activeCallJobId))
  }

  const { user, ascEnabled } = useAppSelector((state) => state.auth)
  const { data: profile } = useUserProfile(user?.id)
  const assistedCallEnabled = profile?.assistedCallEnabled

  const [showStandardCall, setShowStandardCall] = useState(false)
  const [showCallStartedModal, setShowCallStartedModal] = useState<null | { type: CallTypeEnum; callId: string }>(null)
  const [startingCall, setStartingCall] = useState<null | {
    callId: string
    type: CallTypeEnum
  }>(null)
  const [showPayerCallRequestModal, setShowPayerCallRequestModal] = useState(false)
  const [selectedCallType, setSelectedCallType] = useState<{ type: CallTypeEnum; useCase: UseCaseEnum }>()

  const { data: call } = useCall(startingCall?.callId, {
    refreshInterval: RefreshIntervalsEnum.useCallForTalkToPayorRepCalls,
  })
  const { data: userPhonePreference } = useUserPreference(UserPreferencesKeysEnum.softphonePhoneNumbers)
  const userPhoneData = JSON.parse(userPhonePreference?.value || '{}')
  const isCallWithTakeOverDisabled =
    ['denied', 'deniedByOS'].includes(microphonePermissionStatus) &&
    (!userPhoneData?.currentSelection || userPhoneData.currentSelection === DEFAULT_PAYER_VA_SOFTPHONE_KEY)

  useEffect(() => {
    if (startingCall && call) {
      if (call?.status === CallStatusEnum.InProgress) {
        setShowCallStartedModal(startingCall)
        setStartingCall(null)
      }

      if (call?.status === CallStatusEnum.FailedToQueue) {
        notificator.notify({
          heading: 'Call failed to queue, please report this to the administrator',
          type: 'error',
        })
      }
      if (call?.status === CallStatusEnum.FailedToComplete) {
        notificator.notify({
          heading: 'Call failed to complete, please report this to the administrator',
          type: 'error',
        })
        //notificator.error('Call failed to complete, please report this to the administrator')
      }
      if (call?.status === CallStatusEnum.RetryPending) {
        notificator.notify({
          heading: 'Call attempt failed. Call retry is pending',
          type: 'error',
        })
        //notificator.error('Call attempt failed. Call retry is pending')
      }
    }
  }, [startingCall, call])

  const getSupportedOperations = (value: string) => {
    const levelofSupport = JSON.parse(localStorage.getItem('levelOfSupport')!)
    const filteredLevel = levelofSupport.filter((level: ILevelOfSupport) => level?.levelOfSupport == value)
    return filteredLevel[0]?.supportedOperations ? filteredLevel[0]?.supportedOperations : []
  }

  const handleUserResponsePayerRequest = (callRequest: boolean) => {
    setShowPayerCallRequestModal(callRequest)
    if (callRequest && selectedCallType) {
      setShowPayerCallRequestModal(false)
      handleStartCall(selectedCallType?.type, selectedCallType?.useCase).call(() =>
        console.log('Payer not available but call started'),
      )
    }
  }

  const handleStartCall = (type: CallTypeEnum, useCase: UseCaseEnum) => async () => {
    try {
      if (!claim) return

      const jobResponse: ICall = await startCall(claim.oaiClaimId, { type, useCase })

      if (notificator.isDesktopNotificationsNotSelected()) notificator.getDesktopNotificationPermission()

      //TODO When we will focus on performance we will create a global mutator that receives an array of desired mutations and we will mutate only locally
      await revalidateAllSwrCache()
      if ([CallTypeEnum.attendable, CallTypeEnum.humanAgent].includes(type)) {
        dispatch(updateHumanAgentCallInProgress({ callInProgress: true }))
        dispatch(
          updateLiveCall({
            activeCallJobId: jobResponse.jobId,
            type,
            activeClaimId: claim?.oaiClaimId,
            claimId: claim?.claimId,
            activeCallJbId: jobResponse.jbId,
          }),
        )
      } else if ([CallTypeEnum.standard, CallTypeEnum.diagnosticHeadset].includes(type)) {
        dispatch(updateHumanAgentCallInProgress({ callInProgress: true }))
        dispatch(
          updateLiveCall({
            activeCallJobId: jobResponse.jobId,
            type,
            activeClaimId: claim?.oaiClaimId,
            claimId: claim?.claimId,
            activeCallJbId: jobResponse.jbId,
            automationType: AutomationTypeEnum.basic,
          }),
        )
        redirectToLiveCalls(jobResponse.jobId)
      }
      setStartingCall({ type, callId: jobResponse.jobId })
    } catch (error: any) {
      errorHandler.handleError(error, ErrorMessagesEnum.StartCall)
    }
  }

  const supportedOperations = getSupportedOperations(claim?.levelOfSupport)
  const isGetClaimStatusDisabled = supportedOperations
    ? !supportedOperations?.includes(callSupportedOperations.FetchECS)
    : true

  const hasLevelofSupportGetaRep = supportedOperations
    ? supportedOperations?.includes(callSupportedOperations.GetARep)
    : false

  const isGetPayorRepDisabled = !hasLevelofSupportGetaRep || (activeCallJobId ? true : false) || !assistedCallEnabled

  /* const isGetEBVDisabled =
    activeCallJobId || supportedOperations ? !supportedOperations.includes(callSupportedOperations.FetchEBV) : true */

  const isGetEOBDisabled =
    activeCallJobId || supportedOperations ? !supportedOperations.includes(callSupportedOperations.FetchEOB) : true

  const isASCDisabled =
    !ascEnabled ||
    (activeCallJobId || supportedOperations ? !supportedOperations.includes(callSupportedOperations.FetchASC) : true)

  const checkPayerAvailability = (type: CallTypeEnum, useCase: UseCaseEnum) => {
    if (isCallWithTakeOverDisabled && type === CallTypeEnum.humanAgent) {
      notificator.notify({
        heading: 'Microphone access is disabled',
        message: (
          <>
            In order to start assisted calls, you will need to enable microphone access in your browser. To learn more,{' '}
            <a
              href={getUnblockMicHelperUrl(getBrowser())}
              className="text-decoration-underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              click here
            </a>
          </>
        ),
        type: 'warn',
        toastOptions: { hideProgressBar: true, toastId: 'micBlockedNotification' },
      })
      return
    }

    setSelectedCallType({
      type: type,
      useCase: useCase,
    })
    if (!claim?.insurerPhoneNumber) {
      sendLogs({ message: 'ClaimCallMenu.tsx ~ checkPayerAvailability ~ insurerPhoneNumber:undefined' })
      return
    }
    const query = apiPaths.phoneNumber(
      `${claim?.insurerPhoneNumber}/${useCase}/${type === CallTypeEnum.humanAgent ? true : false}/isActive`,
    )

    getPayerAvailability(query)
      .then(({ isActive }) => {
        if (isActive) {
          handleStartCall(type, useCase).call(() => console.log('Payer available call started'))
        } else {
          setShowPayerCallRequestModal(true)
        }
      })
      .catch((error: any) => {
        const message = error.response.data.detail
        const { status } = error.response

        if (status === 404) {
          handleStartCall(type, useCase).call(() => console.log('Phone number not found call started'))
          return
        }
        notificator.notify({
          heading: message,
          type: 'error',
        })
        //notificator.error(message)
      })
  }

  const callMenuItems = [
    ...(ClaimStatusTypeEnum.diagnosticClaim === claim?.oaiStatus
      ? [
          {
            text: <span className="d-flex gap-2 align-items-center">Perform Diagnostic Call</span>,
            onClick: handleStartCall(CallTypeEnum.diagnosticHeadset, UseCaseEnum.CSI),
          },
        ]
      : [
          {
            text: (
              <span className="d-flex gap-2 align-items-center">
                {formatCallTypeLabel(CallTypeEnum.unattended, UseCaseEnumToUseCaseIdEnum.CSI)}
              </span>
            ),
            onClick: () => checkPayerAvailability(CallTypeEnum.unattended, UseCaseEnum.CSI),
            disabled: isGetClaimStatusDisabled,
          },
          ...(EnvironmentFeaturesEnabled.claimCallMenuAttendableCsi
            ? [
                {
                  text: (
                    <span className="d-flex gap-2 align-items-center">
                      {formatCallTypeLabel(CallTypeEnum.attendable, UseCaseEnumToUseCaseIdEnum.CSI)}
                    </span>
                  ),
                  onClick: () => checkPayerAvailability(CallTypeEnum.attendable, UseCaseEnum.CSI),
                  disabled: isGetClaimStatusDisabled,
                },
              ]
            : []),
          ...(!isGetEOBDisabled
            ? [
                {
                  text: (
                    <span className="d-flex gap-2 align-items-center">
                      {formatCallTypeLabel(CallTypeEnum.unattended, UseCaseEnumToUseCaseIdEnum.EOB)}
                    </span>
                  ),
                  onClick: () => checkPayerAvailability(CallTypeEnum.unattended, UseCaseEnum.EOB),
                  disabled: isGetEOBDisabled,
                },
              ]
            : []),
          ...(EnvironmentFeaturesEnabled.claimCallMenuAttendableCsi && !isGetEOBDisabled
            ? [
                {
                  text: (
                    <span className="d-flex gap-2 align-items-center">
                      {formatCallTypeLabel(CallTypeEnum.attendable, UseCaseEnumToUseCaseIdEnum.EOB)}
                    </span>
                  ),
                  onClick: handleStartCall(CallTypeEnum.attendable, UseCaseEnum.EOB),
                  disabled: isGetEOBDisabled,
                },
              ]
            : []),
          ...(EnvironmentFeaturesEnabled.claimCallMenuAttendableWorkersComp
            ? [
                {
                  text: (
                    <span className="d-flex gap-2 align-items-center">
                      {formatCallTypeLabel(CallTypeEnum.attendable, UseCaseEnumToUseCaseIdEnum.WorkersComp)}
                    </span>
                  ),
                  onClick: handleStartCall(CallTypeEnum.attendable, UseCaseEnum.WorkersComp),
                },
              ]
            : []),
          ...(EnvironmentFeaturesEnabled.claimCallMenuAttendableDirectLine
            ? [
                {
                  text: (
                    <span className="d-flex gap-2 align-items-center">
                      {formatCallTypeLabel(CallTypeEnum.attendable, UseCaseEnumToUseCaseIdEnum.DirectLine)}
                    </span>
                  ),
                  onClick: handleStartCall(CallTypeEnum.attendable, UseCaseEnum.DirectLine),
                },
              ]
            : []),
          {
            text: (
              <span className="d-flex gap-2 align-items-center">
                {formatCallTypeLabel(CallTypeEnum.humanAgent, UseCaseEnumToUseCaseIdEnum.CSI)}
              </span>
            ),
            onClick: () => checkPayerAvailability(CallTypeEnum.humanAgent, UseCaseEnum.CSI),
            disabled: isGetPayorRepDisabled,
          },
          {
            text: (
              <span className="d-flex gap-2 align-items-center">
                {formatCallTypeLabel(CallTypeEnum.unattended, UseCaseEnumToUseCaseIdEnum.ASC)}
              </span>
            ),
            onClick: () => checkPayerAvailability(CallTypeEnum.unattended, UseCaseEnum.ASC),
            disabled: isASCDisabled,
          },
          {
            text: (
              <span className="d-flex gap-2 align-items-center">
                {formatCallTypeLabel(CallTypeEnum.humanAgent, UseCaseEnumToUseCaseIdEnum.ASC)}
              </span>
            ),
            onClick: () => checkPayerAvailability(CallTypeEnum.humanAgent, UseCaseEnum.ASC),
            disabled: isASCDisabled,
          },
        ]),
  ]

  return (
    <Div>
      {claim.archived ? (
        <Button disabled={true}>
          <VaCustomAvatar
            style={{
              filter: 'invert(1)',
            }}
          />
          <span className="mx-2">Deploy AI Agent</span>
        </Button>
      ) : (
        <>
          <OBDropdown
            contenttype="text-icon"
            className="call-btn call-dropdown-option"
            variant="primary"
            radius="rounded"
            buttonName={
              <>
                <VaCustomAvatar
                  style={{
                    filter: 'invert(1)',
                  }}
                />
                <span className="mx-1">Deploy AI Agent</span>
              </>
            }
            items={[{ items: callMenuItems }]}
          />
          {showPayerCallRequestModal && (
            <PayerCallRequestConfirmationModal
              callPayer={(val: boolean) => handleUserResponsePayerRequest(val)}
              show={showPayerCallRequestModal}
            />
          )}
        </>
      )}
      <ClaimCallJobStandardModal
        show={showStandardCall}
        setShow={setShowStandardCall}
        phoneNumber={phoneNumber}
        startCall={handleStartCall(CallTypeEnum.standard, UseCaseEnum.NoVirtualAgent)}
        claim={claim}
      />

      {showCallStartedModal && (
        <ClaimCallJobStartedModal
          claimId={claim.oaiClaimId}
          type={showCallStartedModal.type}
          callId={showCallStartedModal.callId}
        />
      )}
    </Div>
  )
}

const Div = styled.div`
  .dropdown-toggle svg {
    position: relative;
    top: -0.05rem;
    color: white;
  }

  .icon-claim {
    color: ${({ theme }) => theme.colors.success.darker};
  }
  .icon-claim[disabled] * {
    color: ${({ theme }) => theme.colors.success.light};
  }
  .icon-payer {
    color: ${({ theme }) => theme.colors.info.darker};
  }

  .dropdown-menu.show {
    z-index: 7;
    padding: 0;
    background-color: ${({ theme }) => theme.colors.neutral.lightest};
    border: 1px solid ${({ theme }) => theme.colors.grey.lighter};
    margin-top: 5px;
    padding: ${({ theme }) => theme.spacing.xs} 0;
    border-radius: ${({ theme }) => theme.borderRadius.rounded};
  }
  .dropdown-submenu {
    width: 14.8125rem;
    text-align: left;
    box-shadow: 0 4px 4px 0 rgb(0 0 0 / 10%);
    border: solid 1px #e5e9f0;
  }
  .dropdown-item:hover {
    background-color: ${({ theme }) => theme.colors.grey.lighter} !important;
    color: ${({ theme }) => theme.colors.neutral.neutral};
  }
  .dropdown-menu .dropdown-item.active {
    background-color: ${({ theme }) => theme.colors.grey.lighter} !important;
    color: ${({ theme }) => theme.colors.neutral.neutral};
  }
  .dropdown-item {
    ${({ theme }) => theme.typography.s.normal};
    padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.s};
  }
  .dropdown-item:disabled {
    color: ${({ theme }) => theme.colors.grey.light}!important;
    cusor: not-allowed;
  }

  .btn-primary {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing.xxs};
    border-radius: ${({ theme }) => theme.borderRadius.rounded};
    outline: none;
    border: none;
    background-color: ${({ theme }) => theme.colors.primary.neutral} !important;
    ${({ theme }) => theme.typography.s.medium};
    border-radius: ${({ theme }) => theme.borderRadius.rounded};
    color: ${({ theme }) => theme.colors.neutral.lightest};
    padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.xs};
  }
  .show > .btn-primary.dropdown-toggle {
    background-color: ${({ theme }) => theme.colors.primary.darker} !important;
    border: transparent !important;
    color: ${({ theme }) => theme.colors.info.lightest} !important;
  }
  .btn-primary:click {
    border: none !important;
    background-color: ${({ theme }) => theme.colors.primary.darker}!important;
    color: ${({ theme }) => theme.colors.info.lightest} !important;
  }
  .btn-primary:focus {
    border: none !important;
    background-color: ${({ theme }) => theme.colors.primary.darker}!important;
    color: ${({ theme }) => theme.colors.info.lightest} !important;
    box-shadow: none !important;
  }
  .btn-primary:hover {
    border: transparent !important;
    background-color: ${({ theme }) => theme.colors.primary.dark}!important;
    color: ${({ theme }) => theme.colors.info.lightest} !important;
  }
  .btn-primary::before {
    display: none;
  }
`
